import * as FileSaver from 'file-saver';
import isNil from 'lodash/isNil';
import cloneDeep from 'lodash/cloneDeep';
import { FieldValues } from 'react-hook-form';
import { t } from 'i18next';

import { RouteInfo } from '@topconagriculture/frontend-utils';

import { notificationsConfig } from '../../../config/constants';
import { UserPersonas } from '../../../store/user/user.types';
import { ReportFilterSettings, ReportFilterSettingsItem } from '../../../store/entities/reports/reports.types';
import { ReportSettingsFormValue } from '../../../shared/forms/ReportSettingsForm/ReportSettingsForm';
import { formatDate } from './dateUtils';
import { ModalsNames } from '../../../config/modals/modalNames';
import { Forms } from '../../../store/form/form.selector';
import { Group } from '../../../store/entities/group/group.types';
import { recipesSelector } from '../../../store/selectors';
import { InventoryReportType, ReportType } from '../../../store/entities/reports/reports.constants';
import { reportsPaths } from '../../../routes/Reports/reportsPaths';
import { inventoryPaths } from '../../../routes/Inventory/inventoryPaths';
import { refusalsPaths } from '../../../routes/Refusals/refusalsPaths';
import { pushHistoryByUrl } from '../../../store/router/router.actions';
import { DataToolsModalParams } from '../../../hooks/useDataToolsModalParams';
import { dataToolsPaths } from '../../../routes/Manage/DataTools/dataToolsPaths';
import { DataToolsType } from '../../../store/entities/dataTools/dataTools.constants';
import { AppliedPer, ChargePeriod } from '../../../store/entities/costTypes/costTypes.types';

export const composeErrorMessage = (err) => {
  const genericErrObj = {
    translationKey: 'app:generic_error',
    ...notificationsConfig.defaultErrorConf,
  };
  if (err && err.data) {
    if (err.data.message || err.data.ExeptionMessage || err.data.errorMessage) {
      return {
        title: err.data.message || err.data.ExeptionMessage || err.data.errorMessage,
        ...notificationsConfig.defaultErrorConf,
      };
    }
  }
  return genericErrObj;
};

/**
 * Given a max load and a size, calculates the density
 * @param maxLoad
 * @param size
 */
export const calculateDensity = (maxLoad: number, size: number) => {
  if (Number(size) === 0) {
    return 0;
  }
  const density = maxLoad / size;
  return Number(density) > 0 ? density : 0;
};

/**
 * Given a number of minutes, calculates the total seconds
 * @param minutes
 * @param seconds
 */
export const calculateTotalMinutes = (minutes: number, seconds = 0) => {
  return Math.floor(minutes * 60) + seconds;
};

/**
 * Calculate the sum of a specific property in an array of objects.
 * We use it to calculate the sum of actual loads or dry matters of ingredients in a recipe
 * @param arr
 * @param prop
 * @param value
 */
export const calculateTotal = (arr, prop, value) => arr.reduce((acc, curr) => curr[prop] + acc, value);

/**
 * It composes the ingredients subtext visible in sidebars with description, percentage, price and
 * proper separators and metrics
 * @param description
 * @param ingredientType
 */
export const composeIngredientSubText = (description: string, ingredientType?: string, dm?: number) => {
  const ingTypeTxt = ingredientType ? ` | ${ingredientType}` : '';

  return description + ingTypeTxt;
};

/**
 * It composes the ration subtext visible in sidebars with description, percentage, amount and
 * proper separators and metrics
 * @param description
 * @param amount
 * @param unit
 */
export const composeRationSubText = (description: string, amount?: number, unit?: string) => {
  // const dryMatterTxt =
  //   String(dryMatterPerc) !== '' ? ` | ${toDecimal(dryMatterPerc, 0) || 0} %` : '';
  const amountTxt = amount ? ` | ${amount.toFixed(2)} ${unit}` : '';

  return description + amountTxt;
};

///// GENERIC UTILITIES /////
/***
 * Useful in translations
 */
String.prototype.toCapFirstCase = function () {
  return this.charAt(0).toUpperCase() + this.slice(1);
};

/**
 * Transform a number to a decimal with specific number of decimal digits
 */
export const toDecimal = (value?: number, decimals = 2) => {
  const multiplier = Math.pow(10, decimals);
  return typeof value === 'number' && isFinite(value) ? Math.round(value * multiplier) / multiplier : '';
};

/**
 * Transform number or string to string with specified decimal digits
 */
export const toPrecisionSting = (value: string | number, decimals = 2) => {
  const multiplier = Math.pow(10, decimals);
  return (Math.round(+value * multiplier + Number.EPSILON) / multiplier).toString();
};
export const normalizeNumberInput =
  (min = 0, max: number | null = null) =>
  (given: number | string) => {
    const value = typeof given === 'number' ? given.toString() : given;

    if (value === '') {
      return '';
    }
    if (+value < min) {
      return min.toString();
    }
    if (max && +value > max) {
      return max.toString();
    }

    return value;
  };

/**
 * Given an ingredient type matches an icon name.
 * @param ingredientTypeCode
 */
export const getIngredientIcon = (ingredientTypeCode: number): string => {
  const iconNamesFromIngredienType = {
    0: 'ingredients',
    1: 'roughage',
    2: 'by-product',
    3: 'concentrate',
    4: 'fresh-grass',
    5: 'mineral',
    6: 'medication',
  };

  return iconNamesFromIngredienType[ingredientTypeCode] || 'ingredients';
};

export const isMedicine = (ingredientTypeCode: number): boolean => ingredientTypeCode === 6;

export const getRoutesByUserPersonas = (sectionRoutes, userPersonas?: UserPersonas): RouteInfo[] => {
  const publicRoutes = sectionRoutes ? sectionRoutes.shared : [] || [];
  const personasRoutes = userPersonas ? sectionRoutes[userPersonas] : [];
  return [...publicRoutes, ...(personasRoutes || [])].filter(Boolean);
};

/**
 * Trims extraneous whitespace from string
 * @example
 * trimWhitespace('  one  two     maybe..  ') // --> 'one two maybe..'
 */
export const trimWhitespace = (val: string) => val.replace(/\s\s+/g, ' ').replace(/^\s+|\s+$/g, '');

export const isNumeric = (val: string | number | null | undefined) =>
  isNil(val) ? false : /-?(\d+|\d+\.\d+|\.\d+)([eE][-+]?\d+)?/.test(val!.toString());

export const noLeadingZeroInt = (val: string | number | null | undefined) =>
  isNil(val) ? '' : isNumeric(val) ? parseInt(val!.toString(), 10) : '';

/***
 * Rounded to 2 decimals
 * @param val
 */
export const noLeadingZeroFloat = (val: string | number | null | undefined, round = true) => {
  if (isNil(val) || !isNumeric(val)) {
    return '';
  }
  const parsed = parseFloat(val!.toString());
  return Number(round ? Math.round(parsed * 100) / 100 : parsed);
};

export const noTrailingZeroFloat = (val: string | number | null | undefined, precision = 2) => {
  if (isNil(val) || !isNumeric(val)) {
    return '';
  }
  const numberValue = val!.toString();
  if (isNaN(parseFloat(numberValue)) === false) {
    let toFixedLength = 0;
    const str = String(numberValue);

    ['.', ','].forEach((seperator) => {
      const arr = str.split(seperator);
      if (arr.length === 2) {
        toFixedLength = arr[1].length > precision ? precision : arr[1].length;
      }
    });

    const parsed = parseFloat(str).toFixed(toFixedLength);

    if (toFixedLength === precision) {
      return parsed.replace(/0*$|\.0*$/, '');
    }

    return parsed;
  }

  return numberValue;
};

/**
 * Moves an array item to a target position
 */
export const moveArrayItem = <T>(items: T[], from: number, to: number): T[] => {
  const reorderedItems = Array.from(items);
  const [moved] = reorderedItems.splice(from, 1);
  reorderedItems.splice(to, 0, moved);

  return reorderedItems;
};

/**
 * Copies an item from one array to another using indexes as arguments
 */
export const copyArrayItemToArray = <T>(source: T[], target: T[], from: number, to: number): T[] => {
  const sourceList = Array.from(source);
  const targetList = Array.from(target);

  const item = cloneDeep(sourceList[from]);
  targetList.splice(to, 0, item);

  return targetList;
};

export const moveArrayItemBetween = <T>(source: T[], target: T[], from: number, to: number): [T[], T[]] => {
  const sourceList = Array.from(source);
  const targetList = Array.from(target);

  const [movedItem] = sourceList.splice(from, 1);
  targetList.splice(to, 0, movedItem);

  return [sourceList, targetList];
};

export const filterByStringArray = (arr: (string | undefined)[] = [], criteria: string) =>
  arr.some((str) => (str ? str.toLocaleLowerCase().includes(criteria.toLocaleLowerCase()) : true));

export const formatNumberByRegion = (num: number, decimal?: number) => {
  if (!isNumeric(num) || typeof num === 'string') {
    return num;
  }

  const decimalSeparator = getDecimalSeparator();
  const thousandSeparator = getThousandSeparator();
  const [intPart, decimalPart] = String(decimal !== undefined ? num.toFixed(decimal) : num).split('.');
  return `${intPart.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousandSeparator}`)}${
    decimalPart ? `${decimalSeparator}${decimalPart}` : ''
  }`;
};

export const getDecimalSeparator = () => {
  return getSeparator('decimal');
};

export const getThousandSeparator = () => {
  return getSeparator('group');
};

export function getSeparator(separatorType: string) {
  const numberWithGroupAndDecimalSeparator = 1000.1;
  const separator = Intl.NumberFormat(navigator.language)
    .formatToParts(numberWithGroupAndDecimalSeparator)
    .find((part) => part.type === separatorType);
  return separator ? separator.value : ' ';
}

export const sortNestedArrayByProp = (a, b, props: string) => {
  if (a[0][props] < b[0][props]) {
    return -1;
  }
  if (b[0][props] < a[0][props]) {
    return 1;
  }
  return 0;
};

export const generateReportFilename = (filename: string): string => {
  return `${filename}_${formatDate(new Date(), 'yyyy-MM-dd:HH-mm-ss')}`;
};

const includedParamsMaper = {
  includedDrivers: 'drivers',
  includedGroupTypes: 'groupTypes',
  includedGroups: 'groups',
  includedIngredientTypes: 'ingredientTypes',
  includedIngredients: 'ingredients',
  includedMixers: 'mixers',
  includedRations: 'rations',
  includedLots: 'lots',
  includedLotTypes: 'lotTypes',
  includedRelations: 'relations',
  includedStockTransactionTypes: 'stockTransactionTypes',
};

const isArraysEqual = <T>(arr1: T[], arr2: T[]): boolean => {
  if (arr1.length !== arr2.length) return false;
  for (const x of arr1) {
    if (!arr2.includes(x)) return false;
  }
  return true;
};

export const filterReportSettings = (
  formSettings: ReportSettingsFormValue,
  defaultSettings: ReportFilterSettings,
): ReportSettingsFormValue => {
  if (!formSettings) return {};
  const result = cloneDeep(formSettings) as ReportSettingsFormValue;
  for (const key of Object.keys(includedParamsMaper)) {
    if (
      formSettings[key] &&
      defaultSettings[includedParamsMaper[key]] &&
      isArraysEqual(
        formSettings[key],
        defaultSettings[includedParamsMaper[key]].map((item: ReportFilterSettingsItem) => item.id),
      )
    ) {
      delete result[key];
    }
  }
  return result;
};

export const handleInput = (input: string, decimals?: number, max?: number) => {
  const ds = getDecimalSeparator();
  let value = input.replace(',', '.');
  const [intPart, decPart] = value.split('.');
  if (decPart && decimals && decPart.length > decimals) {
    value = `${intPart}.${decPart.substr(0, 3)}`;
  }
  value = value.replace(/^0+/, '') || '0';
  if (value.startsWith('.')) {
    value = `0${value}`;
  }

  if (+value || value === '0' || value === '0.' || +value === 0) {
    let valueClamped = Math.abs(+value);
    if (max) {
      valueClamped = Math.min(valueClamped, max);
    }
    if (+value !== valueClamped) {
      value = valueClamped.toString();
    }
    return {
      value: +value,
      valueStr: value.replace('.', ds),
    };
  }
  return null;
};

export const handleInputInt = (input: string, max?: number, min = 0) => {
  const inputTrailed = input.replace(/^0+/, '');
  if (!inputTrailed) return min;

  const value = +inputTrailed;
  if (value && value.toString() === inputTrailed) {
    const intValue = parseInt(input, 10);
    if (value === intValue) {
      const val = Math.max(Math.abs(value), min);
      return max ? Math.min(val, max) : val;
    }
    return null;
  }
  return null;
};

export const saveFile = (res: any, fileFormat: string, ext: string, fileName = 'TMR-Report') => {
  const blob = new Blob([res.data], { type: fileFormat });
  FileSaver.saveAs(blob, `${fileName}.${ext}`);
};

const dateFormatOptions: Intl.DateTimeFormatOptions = {
  year: '2-digit',
  day: '2-digit',
  month: '2-digit',
};

const timeFormatOptions: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
};

const fullTimeFormatOptions: Intl.DateTimeFormatOptions = {
  ...timeFormatOptions,
  second: '2-digit',
};

const formatPartMapper = (x: Intl.DateTimeFormatPart) => {
  if (x.type === 'year') {
    return 'yyyy';
  } else if (x.type === 'month') {
    return 'MM';
  } else if (x.type === 'day') {
    return 'dd';
  } else if (x.type === 'hour') {
    return 'HH';
  } else if (x.type === 'minute') {
    return 'mm';
  } else if (x.type === 'second') {
    return 'ss';
  } else if (x.type === 'dayPeriod') {
    return 'a';
  } else {
    return x.value;
  }
};

export const getDateFormat = () => {
  const formatter = Intl.DateTimeFormat(navigator.language, dateFormatOptions);
  const parts = formatter.formatToParts(new Date());
  return parts.map((x) => formatPartMapper(x)).join('');
};

export const getTimeFormat = (withSeconds?: boolean) => {
  const formatter = Intl.DateTimeFormat(navigator.language, withSeconds ? fullTimeFormatOptions : timeFormatOptions);
  const parts = formatter.formatToParts(new Date());
  const partsMapped = parts.map((x) => formatPartMapper(x));
  const pattern = partsMapped.join('');
  return partsMapped.includes('a') ? pattern.replace('HH', 'hh') : pattern;
};

export const getDateTimeFormat = (withSeconds?: boolean) => {
  const formatter = Intl.DateTimeFormat(navigator.language, {
    ...dateFormatOptions,
    ...(withSeconds ? fullTimeFormatOptions : timeFormatOptions),
  });
  const parts = formatter.formatToParts(new Date());
  const partsMapped = parts.map((x) => formatPartMapper(x));
  const pattern = partsMapped.join('');
  return partsMapped.includes('a') ? pattern.replace('HH', 'hh') : pattern;
};

export const renderFeedingListTableModal = (
  modalName: string,
  openModal: (params: any) => void,
  onExit: (params: any) => void,
) => {
  openModal({
    modalName: ModalsNames.feedingListTableModal,
    modalParams: {
      formName: Forms.feedingListTableForm,
      onExit: onExit,
    },
  });
};

export const renderModal = (
  modalName: string,
  openModal: (params: any) => void,
  closeModal: () => void,
  goTo: (path: string) => void,
  menuPath: string,
): void => {
  openModal({
    modalName,
    modalParams: {
      onExit: () => {
        closeModal();
        goTo(menuPath);
      },
    },
  });
};

export const extendIngredients = (values: number[], transitionDays: number[]) => {
  return values.reduce((acc, value, index, arr) => {
    if (index + 1 === arr.length) return acc.concat(value);
    if (index + 1 < arr.length) {
      const step = (arr[index + 1] - value) / (transitionDays[index + 1] - transitionDays[index]);
      const array = [] as any;
      for (let i = 0; i < transitionDays[index + 1] - transitionDays[index]; i++) {
        array.push(value + i * step);
      }
      return acc.concat(array);
    }
    return acc;
  }, [] as number[]);
};

export const createTimezoneObjectWithTranslation = (timezones: string[], t) => {
  return timezones.reduce((prev, curr) => ({ ...prev, [curr]: t(`settings:${curr}`) }), {});
};

type UnknownArrayOrObject = unknown[] | Record<string, unknown>;

export const getDirtyValues = (
  dirtyFields: UnknownArrayOrObject | boolean,
  allValues: UnknownArrayOrObject,
): UnknownArrayOrObject => {
  // NOTE: Recursive function.

  // If *any* item in an array was modified, the entire array must be submitted, because there's no
  // way to indicate "placeholders" for unchanged elements. `dirtyFields` is `true` for leaves.
  if (dirtyFields === true || Array.isArray(dirtyFields)) {
    return allValues;
  }

  // Here, we have an object.
  return Object.fromEntries(
    Object.keys(dirtyFields).map((key) => [key, getDirtyValues(dirtyFields[key], allValues[key])]),
  );
};

export const createFeedingListRequestData = (
  dirtyValues: UnknownArrayOrObject,
  formValues: FieldValues,
  groups: Group[],
) => {
  const globalFeedingFactorEnemies = ['actualWeightTotal', 'dryWeightTotal', 'actualWeightHead', 'dryWeightHead'];

  return Object.entries(dirtyValues)
    .map(([key, value]) => {
      const idStartIndex = key.indexOf('-') + 1;
      const id = key.substring(idStartIndex);
      let fieldName = key.substring(0, idStartIndex - 1);
      const isGlobalFeedingFactorEnemy = globalFeedingFactorEnemies.some((enemy) => fieldName.includes(enemy));

      if (isGlobalFeedingFactorEnemy) {
        // https://tps-wiki.topcon.com/pages/viewpage.action?spaceKey=TMRT&title=Pen+feedings
        //In final save: always use globalFeedingFactor. Do not use actualWeightTotal, dryWeightTotal,
        // actualWeightHead or dryWeightHead because they can be modified by a change in one of the feedings.
        fieldName = 'globalFeedingFactor';
      }

      if (fieldName.includes('feedingFactor')) {
        // unchanged feeding item id has to be included in patch. changed values contain changed feedingFactor field
        const feedingsList = groups
          .find((group) => group.id === id)
          ?.feedings?.map(({ id, feedingNo }) => ({ id, feedingNo }));

        const feedingsToPatch = feedingsList?.map((feeding) => ({
          id: feeding.id,
          feedingFactor: transformFormValueToNumber(formValues[`feedingFactor${feeding.feedingNo}-${id}`]),
        }));

        return {
          id,
          feedings: feedingsToPatch,
        };
      }

      return {
        id,
        [fieldName]: isGlobalFeedingFactorEnemy
          ? formValues[`globalFeedingFactor-${id}`]
          : // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            value && transformFormValueToNumber(value),
      };
    })
    .reduce((prev: any[], curr) => {
      if (prev.some(({ id }) => id === curr.id)) {
        const targetIndex = prev.findIndex(({ id }) => id === curr.id);
        prev[targetIndex] = { ...prev[targetIndex], ...curr };
        return prev;
      } else {
        return [...prev, curr];
      }
    }, []);
};

export const createFeedingListFormValues = (prevValue, curValue: Group) => {
  const { id, feedings = [] } = curValue;

  const feedingFactors = feedings.reduce(
    (prev, curr) => ({
      ...prev,
      [`feedingFactor${curr.feedingNo}-${id}`]: curr.feedingFactor,
    }),
    {},
  );

  return {
    ...prevValue,
    [`globalFeedingFactor-${id}`]: curValue.globalFeedingFactor,
    [`numberOfCows-${id}`]: curValue.numberOfCows,
    [`actualWeightHead-${id}`]: curValue.actualWeightHead,
    [`actualWeightTotal-${id}`]: curValue.actualWeightTotal,
    [`dryWeightHead-${id}`]: curValue.dryWeightHead,
    [`dryWeightTotal-${id}`]: curValue.dryWeightTotal,
    ...feedingFactors,
  };
};

export const numericStringComapreFunction = (a: string, b: string): number => {
  return a?.localeCompare(b, undefined, {
    numeric: true,
    sensitivity: 'base',
  });
};

export const sortFeedingList = (
  groups: Group[],
  sortingColumn = 'description',
  getRecipeById: ReturnType<typeof recipesSelector.getRecipeById>,
) => {
  const compareFunc = (a: string, b: string): number => {
    if (!a?.length && !b?.length) {
      return 0;
    }

    if (!a?.length) {
      return 1;
    }

    if (!b?.length) {
      return -1;
    }

    return a.localeCompare(b, undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  };

  const getRationName = (group: Group): string =>
    group.rationIds?.map((id) => getRecipeById(id)?.displayName).join(', ') || '';

  const getFeedingFactor = (group: Group) =>
    group.feedings
      ?.find(({ feedingNo }) => feedingNo.toString() === sortingColumn.replace(/\D/g, ''))
      ?.feedingFactor?.toString() || '';

  return groups.sort((a, b) => {
    // method to detect number in a string and then sort it right way
    if (sortingColumn === 'rationNames') {
      const aRationName = getRationName(a);
      const bRationName = getRationName(b);
      return compareFunc(aRationName, bRationName);
    }

    if (sortingColumn.includes('feedingFactor')) {
      const aFeeding = getFeedingFactor(a);
      const bFeeding = getFeedingFactor(b);

      return compareFunc(aFeeding, bFeeding);
    }

    return compareFunc(a[sortingColumn]?.toString(), b[sortingColumn]?.toString());
  });
};

export const transformFormValueToNumber = (value: string | number): number => {
  if (typeof value === 'number') {
    return value;
  }

  return parseFloat(value?.toString()?.replaceAll(getThousandSeparator(), '')?.replace(getDecimalSeparator(), '.'));
};

export const getWeekdays = (t: (string) => string | string[]) => [
  { value: 1, label: t('dates:weekdaysLong')[1] },
  { value: 2, label: t('dates:weekdaysLong')[2] },
  { value: 3, label: t('dates:weekdaysLong')[3] },
  { value: 4, label: t('dates:weekdaysLong')[4] },
  { value: 5, label: t('dates:weekdaysLong')[5] },
  { value: 6, label: t('dates:weekdaysLong')[6] },
  { value: 7, label: t('dates:weekdaysLong')[0] },
];

export const stringifyNumber = (n: number): string => {
  const special = [
    'zeroth',
    'first',
    'second',
    'third',
    'fourth',
    'fifth',
    'sixth',
    'seventh',
    'eighth',
    'ninth',
    'tenth',
    'eleventh',
    'twelfth',
    'thirteenth',
    'fourteenth',
    'fifteenth',
    'sixteenth',
    'seventeenth',
    'eighteenth',
    'nineteenth',
  ];
  const deca = ['twent', 'thirt', 'fort', 'fift', 'sixt', 'sevent', 'eight', 'ninet'];

  if (n < 20) return special[n];
  if (n % 10 === 0) return deca[Math.floor(n / 10) - 2] + 'ieth';
  return deca[Math.floor(n / 10) - 2] + 'y-' + special[n % 10];
};

interface ChangeReportHandlerProps {
  value: any;
  reportsCallback: () => void;
  goTo: typeof pushHistoryByUrl;
  refusalsCallback: () => void;
  openModal: (modalParams: DataToolsModalParams) => void;
  modalParams: DataToolsModalParams;
  showModal: boolean;
}

export const changeReportHandler = ({
  value,
  reportsCallback,
  goTo,
  refusalsCallback,
  openModal,
  modalParams,
  showModal,
}: ChangeReportHandlerProps): void => {
  if (Object.values(ReportType).includes(value)) {
    reportsCallback();
    goTo(reportsPaths.specific.replace(':reportType', value));
    return;
  }

  if (Object.values(InventoryReportType).includes(value)) {
    if (value === InventoryReportType.InventoryProjectedRequirements) {
      goTo(inventoryPaths.projectedRequirements);
      return;
    }
    if (value === InventoryReportType.Inventory) {
      reportsCallback();
      goTo(inventoryPaths.overview);
      return;
    }
    if (value === InventoryReportType.StockTransaction) {
      reportsCallback();
      goTo(inventoryPaths.transactions);
      return;
    }
  }
  if (value === 'refusals') {
    refusalsCallback();
    goTo(refusalsPaths.root);
    return;
  }

  if (Object.values(DataToolsType).includes(value)) {
    if (showModal) {
      openModal(modalParams);
      return;
    }

    goTo(dataToolsPaths.specific.replace(':dataType', value));
  }
};

export const getOrderIngredientsTitles = (t: (string) => string, currency: string) => [
  t('products:ingredient'),
  t('dataTools:amount'),
  t('inventory:unit'),
  `${t('reports:price')} (${currency}) / ${t('reports:unit')}`,
  `${t('feeds:total_price')} (${currency})`,
  t('products:deliver_date'),
  t('inventory:new_price_date'),
];

export const formatAmountAndPriceUnit = (amount: number, priceUnit: string, decimal = 2) => {
  const [priceUnitAmount, priceUnitString] = priceUnit.split(' ');
  return { amount: formatNumberByRegion(amount * +priceUnitAmount, decimal), unit: priceUnitString };
};

const mixingTypeCodeLabels = ['mixers:none', 'mixers:timer_minutes', 'mixers:rotations'];

const mixingStartTypeCodeLabels = ['', 'mixers:manual', 'mixers:auto_start'];

export function getMixingTypeCodeString(field: string, value: string) {
  switch (field) {
    case 'Mix Type':
      return t(mixingTypeCodeLabels[value]);
    case 'Mix Start Type':
      return t(mixingStartTypeCodeLabels[value]);
    default:
      return value;
  }
}

export enum CostTypeDateOptions {
  DATE = 'Date',
  TIMESTAMP = 'Timestamp',
}

export const getCostTypeDateOption = (appliedPer: AppliedPer, chargePeriod: ChargePeriod) => {
  if (
    chargePeriod === ChargePeriod.DAY ||
    (appliedPer === AppliedPer.LOT && chargePeriod === ChargePeriod.ON_SPECIFIC_TIMESTAMP)
  ) {
    return CostTypeDateOptions.DATE;
  }
  return CostTypeDateOptions.TIMESTAMP;
};
